import { useEffect, useState } from "react";
import { Check, Crown, Lock, LogOut, Mail, Package, Percent, Send, Star, TrendingUp, Zap } from "lucide-react";
import { api, eur } from "@/lib/api";
import AdminArenaCampaign from "@/components/AdminArenaCampaign";
import { SITE_IMAGE_DEFAULTS, SITE_MEDIA_GROUPS } from "@/lib/siteImages";
import { DatePickerField, MonthPickerField } from "@/components/DatePickerField";

const TABS = ["orders", "customers", "newsletter", "coupons", "inventory", "reviews", "campaigns", "arena", "email", "report", "settings"];

const ADMIN_TEXTS = {
  en: {
    backOffice: "Back Office", logout: "Logout", signIn: "Sign In", subtitle: "Antalia Pharma — back office",
    stats: { orders: "Orders", revenue: "Revenue", newsletter: "Newsletter" },
    tabs: { orders: "Orders", customers: "Customers", newsletter: "Newsletter", coupons: "Coupons", inventory: "Inventory", reviews: "Reviews", campaigns: "Campaigns", email: "Email", report: "Report", settings: "Settings" },
    th: ["Order", "Date", "Customer", "Items", "Total", "Status", "Shipping"],
    exportCsv: "Export CSV", cartReminders: "Cart reminders", remindersSent: "Reminders sent", remindersFailed: "undeliverable", remindersFail: "Failed to send reminders", sending: "Sending…",
    autoReminders: "Auto reminders", lastRun: "Last auto send", never: "never",
    vipThreshold: "VIP threshold", saved: "Saved", birthday: "Birthday", bdaySaved: "Birthday saved",
    invalidThreshold: "Threshold must be greater than 0", invalidPct: "Invalid value (1-95)",
    noCustomers: "No customers yet.", ordersN: "orders", order1: "order", vipCoupon: "VIP Coupon 15%", vipCreated: "VIP coupon created", vipExists: "Coupon already exists",
    noSubs: "No subscribers yet.",
    newCoupon: "New Coupon", codePh: "CODE (e.g. SPRING15)", create: "Create", deactivate: "Deactivate", inactive: "Inactive",
    couponCreated: "created", couponFail: "Failed to create coupon",
    invTh: ["Product", "Size", "Price", "Stock", "Alert", ""], low: "Low", okLabel: "OK", save: "Save",
    photoSection: "Product photos", changePhoto: "Change photo", photoUpdated: "Photo updated",
    galleryAdd: "+ Add", siteSection: "Site photos",
    siteGroups: { lookbook: "Lookbook page", home: "Home page", about: "About page", science: "Science page", brand: "Brand" },
    resetPhoto: "Reset", customBadge: "Custom",
    slotLabels: { lab: "Laboratory", nature: "Nature", dynamic: "Dynamic bottle", woman: "Portrait", clean: "Clean bottle", soft: "Soft portrait", "home-hero": "Hero image", "home-immune": "Immune category", "home-focus": "Focus category", "home-energy": "Energy category", "home-performance": "Performance category", "about-lab": "Laboratory", "science-lab": "Laboratory", logo: "Logo (header & footer)" },
    blogSection: "Blog photos",
    importCustomers: "Import CSV/Excel", importResult: "imported", importUpdated: "updated", importSkipped: "skipped", importFail: "Import failed (use .csv or .xlsx with an email column)",
    wooSection: "WooCommerce", wooUrl: "Store URL (https://…)", wooKey: "Consumer Key (ck_…)", wooSecret: "Consumer Secret (cs_…)", wooSave: "Save & connect", wooSaved: "Connected", wooTest: "Test", wooOk: "Connection OK", wooImportProducts: "Import products", wooImportCustomers: "Import customers", wooSync: "Sync stock", wooDone: "Done", wooImportOrders: "Import orders (history)", wooWebhooks: "Enable auto-sync (webhooks)", wooHide: "Hide", wooShow: "Show", wooHiddenBadge: "Hidden", wooNote: "Paid orders are pushed to WooCommerce automatically. Stock stays aligned via webhooks or manual sync.",
    campaignSection: "New campaign", campSubject: "Subject", campBody: "Message", campAudience: "Audience", audNews: "Newsletter subscribers", audCust: "Customers", audAll: "Everyone", campRegion: "Region (customers)", campAllRegions: "All regions", campSend: "Send campaign", campSentL: "sent", campFailedL: "failed", campFail: "Send failed", campHistory: "Campaign history", campTh: ["Date", "Subject", "Audience", "Recipients", "Sent", "Failed"], noCampaigns: "No campaigns yet.",
    emailKpis: ["Campaign sent", "Unique opens", "Open rate", "Clicks"], recentOpens: "Latest opens", openedBadge: "Read", opensNote: "Opens tracked from the pre-launch campaign. Apple Mail may preload images, so numbers are indicative.", noOpens: "No opens yet.",
    stripeSection: "Stripe catalog", stripeSync: "Sync Stripe catalog", stripeNote: "Run this after the account switches to live mode (claim): creates products, prices, tax settings and the WELCOME10 coupon in live mode.",
    flashTitle: "Regional flash sale", flashPercent: "Discount %", flashBtn: "Prepare flash sale", flashReady: "created — campaign drafted below, review and send", flashFail: "Flash sale failed",
    audLabels: { newsletter: "Newsletter", customers: "Customers", all: "Everyone" },
    noReviews: "No reviews yet.", approved: "Approved", approve: "Approve", deleteLabel: "Delete",
    salesByProduct: "Sales by product", revenueByMonth: "Revenue by month", sold: "sold",
    fulfillment: { preparing: "Preparing", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled" },
    settingsSections: { loyalty: "Loyalty", reminders: "Cart reminders", birthdays: "Birthday gifts", history: "Send history", launch: "Product launch", chat: "Chat orders", review: "Review requests" },
    chatWa: "WhatsApp number", chatViber: "Viber number", chatPhone: "Company phone", chatCodFee: "COD fee (€)", chatNotify: "Order notifications email", chatNote: "Customers can order via WhatsApp/Viber with a prefilled message. The buttons appear on the site as soon as a number is saved. International format, e.g. +30694…",
    revEnabled: "Ask for a review after delivery", revDays: "Days after delivery", revNote: "A few days after an order is marked as Delivered, the customer automatically receives an elegant email (Greek for Greece, English abroad) with a direct link to leave a review.",
    vipBdayPct: "VIP birthday coupon %", bdayPct: "Birthday coupon %",
    launchDate: "Launch date", launchNote: "On this date the 7 coming-soon products activate automatically and newsletter subscribers receive the launch email.",
    birthdayNote: "Sent automatically on the customer's birthday, in their language.",
    historyTh: ["Date", "Email", "Type", "Status", "Code"], noLog: "No sends yet.",
    typeLabels: { cart_recovery: "Cart reminder", birthday: "Birthday coupon", review_request: "Review request", launch: "Launch", admin_order: "Admin order", welcome: "Welcome" },
    statusLabels: { sent: "Sent", failed: "Failed", undeliverable: "Undeliverable" },
  },
  it: {
    backOffice: "Area Riservata", logout: "Esci", signIn: "Accedi", subtitle: "Antalia Pharma — area riservata",
    stats: { orders: "Ordini", revenue: "Fatturato", newsletter: "Newsletter" },
    tabs: { orders: "Ordini", customers: "Clienti", newsletter: "Newsletter", coupons: "Coupon", inventory: "Magazzino", reviews: "Recensioni", campaigns: "Campagne", email: "Email", report: "Report", settings: "Impostazioni" },
    th: ["Ordine", "Data", "Cliente", "Articoli", "Totale", "Stato", "Spedizione"],
    exportCsv: "Esporta CSV", cartReminders: "Promemoria carrelli", remindersSent: "Promemoria inviati", remindersFailed: "non recapitati", remindersFail: "Invio promemoria fallito", sending: "Invio…",
    autoReminders: "Promemoria automatici", lastRun: "Ultimo invio automatico", never: "mai",
    vipThreshold: "Soglia VIP", saved: "Salvato", birthday: "Compleanno", bdaySaved: "Compleanno salvato",
    invalidThreshold: "La soglia deve essere maggiore di 0", invalidPct: "Valore non valido (1-95)",
    noCustomers: "Nessun cliente.", ordersN: "ordini", order1: "ordine", vipCoupon: "Coupon VIP 15%", vipCreated: "Coupon VIP creato", vipExists: "Coupon già esistente",
    noSubs: "Nessun iscritto.",
    newCoupon: "Nuovo Coupon", codePh: "CODICE (es. PRIMAVERA15)", create: "Crea", deactivate: "Disattiva", inactive: "Inattivo",
    couponCreated: "creato", couponFail: "Creazione coupon fallita",
    invTh: ["Prodotto", "Formato", "Prezzo", "Stock", "Avviso", ""], low: "Basso", okLabel: "OK", save: "Salva",
    photoSection: "Foto prodotti", changePhoto: "Cambia foto", photoUpdated: "Foto aggiornata",
    galleryAdd: "+ Aggiungi", siteSection: "Foto sito",
    siteGroups: { lookbook: "Pagina Lookbook", home: "Pagina Home", about: "Pagina Chi Siamo", science: "Pagina Scienza", brand: "Brand" },
    resetPhoto: "Ripristina", customBadge: "Personalizzata",
    slotLabels: { lab: "Laboratorio", nature: "Natura", dynamic: "Flacone dinamico", woman: "Ritratto", clean: "Flacone minimal", soft: "Ritratto soft", "home-hero": "Immagine hero", "home-immune": "Categoria Immuno", "home-focus": "Categoria Focus", "home-energy": "Categoria Energia", "home-performance": "Categoria Performance", "about-lab": "Laboratorio", "science-lab": "Laboratorio", logo: "Logo (header e footer)" },
    blogSection: "Foto blog",
    importCustomers: "Importa CSV/Excel", importResult: "importati", importUpdated: "aggiornati", importSkipped: "saltati", importFail: "Importazione fallita (usa .csv o .xlsx con colonna email)",
    wooSection: "WooCommerce", wooUrl: "URL negozio (https://…)", wooKey: "Consumer Key (ck_…)", wooSecret: "Consumer Secret (cs_…)", wooSave: "Salva e collega", wooSaved: "Collegato", wooTest: "Test", wooOk: "Connessione OK", wooImportProducts: "Importa prodotti", wooImportCustomers: "Importa clienti", wooSync: "Sincronizza stock", wooDone: "Fatto", wooImportOrders: "Importa ordini (storico)", wooWebhooks: "Attiva sync automatica (webhook)", wooHide: "Nascondi", wooShow: "Mostra", wooHiddenBadge: "Nascosto", wooNote: "Gli ordini pagati qui vengono inviati automaticamente a WooCommerce. Lo stock resta allineato via webhook o sync manuale.",
    campaignSection: "Nuova campagna", campSubject: "Oggetto", campBody: "Messaggio", campAudience: "Destinatari", audNews: "Iscritti newsletter", audCust: "Clienti", audAll: "Tutti", campRegion: "Regione (clienti)", campAllRegions: "Tutte le regioni", campSend: "Invia campagna", campSentL: "inviate", campFailedL: "fallite", campFail: "Invio fallito", campHistory: "Storico campagne", campTh: ["Data", "Oggetto", "Destinatari", "Totale", "Inviate", "Fallite"], noCampaigns: "Nessuna campagna.",
    emailKpis: ["Inviate campagna", "Aperture uniche", "Tasso apertura", "Clic"], recentOpens: "Ultime aperture", openedBadge: "Letta", opensNote: "Aperture tracciate dalla campagna pre-lancio. Apple Mail può precaricare le immagini: numeri indicativi.", noOpens: "Nessuna apertura registrata.",
    stripeSection: "Catalogo Stripe", stripeSync: "Sincronizza catalogo Stripe", stripeNote: "Esegui dopo il passaggio alla modalità live (claim dell'account): crea prodotti, prezzi, imposte e il coupon WELCOME10 nella modalità live.",
    flashTitle: "Flash sale regionale", flashPercent: "Sconto %", flashBtn: "Prepara flash sale", flashReady: "creato — campagna pronta qui sotto, rivedi e invia", flashFail: "Flash sale fallita",
    audLabels: { newsletter: "Newsletter", customers: "Clienti", all: "Tutti" },
    noReviews: "Nessuna recensione.", approved: "Approvata", approve: "Approva", deleteLabel: "Elimina",
    salesByProduct: "Vendite per prodotto", revenueByMonth: "Fatturato per mese", sold: "venduti",
    fulfillment: { preparing: "In preparazione", shipped: "Spedito", delivered: "Consegnato", cancelled: "Annullato" },
    settingsSections: { loyalty: "Fedeltà", reminders: "Promemoria carrelli", birthdays: "Regali di compleanno", history: "Storico invii", launch: "Lancio prodotti", chat: "Ordini via chat", review: "Recensioni post-consegna" },
    chatWa: "Numero WhatsApp", chatViber: "Numero Viber", chatPhone: "Telefono aziendale", chatCodFee: "Costo contrassegno (€)", chatNotify: "Email notifiche ordini", chatNote: "I clienti potranno ordinare via WhatsApp/Viber con messaggio precompilato. I pulsanti appaiono sul sito appena salvi un numero. Formato internazionale, es. +30694…",
    revEnabled: "Chiedi una recensione dopo la consegna", revDays: "Giorni dopo la consegna", revNote: "Qualche giorno dopo che l'ordine è segnato come Consegnato, il cliente riceve automaticamente un'email elegante (greco per la Grecia, inglese per l'estero) con il link diretto per lasciare la recensione.",
    vipBdayPct: "Coupon compleanno VIP %", bdayPct: "Coupon compleanno %",
    launchDate: "Data di lancio", launchNote: "A questa data i 7 prodotti in arrivo si attivano automaticamente e gli iscritti alla newsletter ricevono l'email di lancio.",
    birthdayNote: "Inviato automaticamente il giorno del compleanno, nella lingua del cliente.",
    historyTh: ["Data", "Email", "Tipo", "Esito", "Codice"], noLog: "Nessun invio.",
    typeLabels: { cart_recovery: "Promemoria carrello", birthday: "Coupon compleanno", review_request: "Richiesta recensione", launch: "Lancio", admin_order: "Notifica ordine", welcome: "Benvenuto" },
    statusLabels: { sent: "Inviata", failed: "Fallita", undeliverable: "Non recapitabile" },
  },
};

export default function Admin() {
  const [lang, setLang] = useState(() => localStorage.getItem("ap-admin-lang") || "it");
  const t = ADMIN_TEXTS[lang];
  const [token, setToken] = useState(() => localStorage.getItem("ap-admin-token"));
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [report, setReport] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [exportMonth, setExportMonth] = useState("");
  const [couponForm, setCouponForm] = useState({ code: "", type: "percent", value: "" });
  const [couponMsg, setCouponMsg] = useState("");
  const [stockEdits, setStockEdits] = useState({});
  const [vipMsg, setVipMsg] = useState("");
  const [sendingReminders, setSendingReminders] = useState(false);
  const [settings, setSettings] = useState(null);
  const [thresholdInput, setThresholdInput] = useState("");
  const [thresholdMsg, setThresholdMsg] = useState("");
  const [bdayEdits, setBdayEdits] = useState({});
  const [emailLog, setEmailLog] = useState([]);
  const [emailCenter, setEmailCenter] = useState(null);
  const [bdayPctInputs, setBdayPctInputs] = useState({ vip: "20", reg: "10" });
  const [launchDateInput, setLaunchDateInput] = useState("2027-01-01");
  const [chatForm, setChatForm] = useState({ whatsapp_number: "", viber_number: "", company_phone: "", cod_fee: 0, order_notify_email: "" });
  const [revForm, setRevForm] = useState({ review_requests_enabled: true, review_request_days: 3 });
  const [revMsg, setRevMsg] = useState("");
  const [chatMsg, setChatMsg] = useState("");
  const [stripeMsg, setStripeMsg] = useState("");
  const [stripeBusy, setStripeBusy] = useState(false);
  const [launchMsg, setLaunchMsg] = useState("");
  const [bdayPctMsg, setBdayPctMsg] = useState("");
  const [productImages, setProductImages] = useState({});
  const [photoMsg, setPhotoMsg] = useState("");
  const [productGalleries, setProductGalleries] = useState({});
  const [siteMediaSlots, setSiteMediaSlots] = useState({});
  const [blogPosts, setBlogPosts] = useState([]);
  const [woo, setWoo] = useState(null);
  const [wooForm, setWooForm] = useState({ store_url: "", consumer_key: "", consumer_secret: "" });
  const [wooMsg, setWooMsg] = useState("");
  const [wooBusy, setWooBusy] = useState("");
  const [wooImp, setWooImp] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [regions, setRegions] = useState([]);
  const [campForm, setCampForm] = useState({ subject: "", body: "", audience: "newsletter", region: "" });
  const [flashForm, setFlashForm] = useState({ region: "", percent: 15 });
  const [flashMsg, setFlashMsg] = useState("");
  const [flashBusy, setFlashBusy] = useState(false);
  const [campMsg, setCampMsg] = useState("");
  const [campBusy, setCampBusy] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [bdaySavedFor, setBdaySavedFor] = useState("");
  const [reminderResult, setReminderResult] = useState(null);
  const [tab, setTab] = useState("orders");

  const switchLang = (l) => {
    setLang(l);
    localStorage.setItem("ap-admin-lang", l);
  };

  useEffect(() => {
    if (!token) return;
    const safe = (p, fb) =>
      p.catch((e) => {
        if (e?.response?.status === 401) throw e;
        return fb;
      });
    Promise.all([
      safe(api.adminStats(token), null),
      safe(api.adminOrders(token), []),
      safe(api.adminNewsletter(token), []),
      safe(api.adminCoupons(token), []),
      safe(api.adminInventory(token), []),
      safe(api.adminReviews(token), []),
      safe(api.adminReport(token), null),
      safe(api.adminCustomers(token), []),
      safe(api.adminSettings(token), {}),
      safe(api.adminEmailLog(token), []),
      safe(api.adminProductsImages(token), []),
    ])
      .then(([s, o, n, c, i, r, rep, cust, st, log, imgs]) => {
        setStats(s);
        setOrders(o);
        setSubs(n);
        setCoupons(c);
        setInventory(i);
        setReviews(r);
        setReport(rep);
        setCustomers(cust);
        setSettings(st);
        setThresholdInput(String(st.vip_threshold ?? 200));
        setEmailLog(log);
        setBdayPctInputs({ vip: String(st.vip_birthday_percent ?? 20), reg: String(st.birthday_percent ?? 10) });
        setLaunchDateInput(st.launch_date || "2027-01-01");
        setChatForm({ whatsapp_number: st.whatsapp_number || "", viber_number: st.viber_number || "", company_phone: st.company_phone || "", cod_fee: st.cod_fee ?? 0, order_notify_email: st.order_notify_email || "" });
        setRevForm({ review_requests_enabled: st.review_requests_enabled ?? true, review_request_days: st.review_request_days ?? 3 });
        setProductImages(Object.fromEntries(imgs.map((p) => [p.slug, p.image])));
        setProductGalleries(Object.fromEntries(imgs.map((p) => [p.slug, p.images || []])));
        api.siteMedia().then((m) => setSiteMediaSlots(m.slots || {})).catch(() => {});
        api.posts().then(setBlogPosts).catch(() => {});
        api.adminWooGet(token).then(setWoo).catch(() => {});
        api.adminCampaigns(token).then(setCampaigns).catch(() => {});
        api.adminRegions(token).then(setRegions).catch(() => {});
      })
      .catch(() => {
        localStorage.removeItem("ap-admin-token");
        setToken(null);
      });
  }, [token]);

  useEffect(() => {
    if (tab === "email" && token) {
      api.adminEmailCenter(token).then(setEmailCenter).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await api.adminLogin(form);
      localStorage.setItem("ap-admin-token", res.token);
      setToken(res.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("ap-admin-token");
    setToken(null);
  };

  const setFulfillment = async (id, value) => {
    try {
      await api.adminSetFulfillment(token, id, value);
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, fulfillment: value } : o)));
    } catch { /* keep UI */ }
  };

  const download = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const createCoupon = async (e) => {
    e.preventDefault();
    setCouponMsg("");
    try {
      const payload =
        couponForm.type === "percent"
          ? { code: couponForm.code, percent_off: Number(couponForm.value) }
          : { code: couponForm.code, amount_off: Number(couponForm.value) };
      await api.adminCreateCoupon(token, payload);
      setCouponMsg(`${couponForm.code.toUpperCase()} ${t.couponCreated}`);
      setCouponForm({ code: "", type: "percent", value: "" });
      setCoupons(await api.adminCoupons(token));
    } catch (err) {
      setCouponMsg(err.response?.data?.detail || t.couponFail);
    }
  };

  const deactivateCoupon = async (id) => {
    await api.adminDeactivateCoupon(token, id);
    setCoupons((prev) => prev.map((c) => (c.id === id ? { ...c, active: false } : c)));
  };

  const saveStock = async (slug, caps) => {
    const key = `${slug}:${caps}`;
    const value = Number(stockEdits[key]);
    if (Number.isNaN(value)) return;
    await api.adminSetStock(token, { slug, caps, stock: value });
    setInventory((prev) =>
      prev.map((p) =>
        p.slug === slug
          ? { ...p, variants: p.variants.map((v) => (v.caps === caps ? { ...v, stock: value } : v)) }
          : p
      )
    );
  };

  const createVipCoupon = async (customer) => {
    const base = ("VIP" + customer.email.split("@")[0].replace(/[^a-zA-Z]/g, "").slice(0, 5).toUpperCase() + "15").slice(0, 12);
    try {
      await api.adminCreateCoupon(token, { code: base, percent_off: 15 });
      setVipMsg(`${t.vipCreated}: ${base}`);
      setCoupons(await api.adminCoupons(token));
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      setVipMsg(detail.toLowerCase().includes("already exists") ? `${t.vipExists}: ${base}` : (detail || t.couponFail));
    }
  };

  const uploadPhoto = async (slug, file) => {
    if (!file) return;
    setPhotoMsg("");
    try {
      const res = await api.adminUploadProductImage(token, slug, file);
      setProductImages((prev) => ({ ...prev, [slug]: res.image }));
      setPhotoMsg(`${t.photoUpdated}: ${slug}`);
      setTimeout(() => setPhotoMsg(""), 3000);
    } catch (err) {
      setPhotoMsg(err.response?.data?.detail || t.couponFail);
    }
  };

  const addGalleryImage = async (slug, file) => {
    if (!file) return;
    setPhotoMsg("");
    try {
      const res = await api.adminAddGalleryImage(token, slug, file);
      setProductGalleries((prev) => ({ ...prev, [slug]: res.images }));
      setPhotoMsg(`${t.photoUpdated}: ${slug}`);
      setTimeout(() => setPhotoMsg(""), 3000);
    } catch (err) {
      setPhotoMsg(err.response?.data?.detail || t.couponFail);
    }
  };

  const removeGalleryImage = async (slug, url) => {
    try {
      const res = await api.adminRemoveGalleryImage(token, slug, url);
      setProductGalleries((prev) => ({ ...prev, [slug]: res.images }));
    } catch { /* keep UI */ }
  };

  const uploadSiteMedia = async (slot, file) => {
    if (!file) return;
    setPhotoMsg("");
    try {
      const res = await api.adminUploadSiteMedia(token, slot, file);
      setSiteMediaSlots((prev) => ({ ...prev, [slot]: res.image }));
      setPhotoMsg(`${t.photoUpdated}: ${slot}`);
      setTimeout(() => setPhotoMsg(""), 3000);
    } catch (err) {
      setPhotoMsg(err.response?.data?.detail || t.couponFail);
    }
  };

  const resetSiteMedia = async (slot) => {
    setSiteMediaSlots((prev) => {
      const next = { ...prev };
      delete next[slot];
      return next;
    });
    try {
      await api.adminResetSiteMedia(token, slot);
    } catch { /* keep UI */ }
  };

  const resetPhoto = async (slug) => {
    try {
      const res = await api.adminResetProductImage(token, slug);
      setProductImages((prev) => ({ ...prev, [slug]: res.image }));
      setPhotoMsg(`${t.photoUpdated}: ${slug}`);
      setTimeout(() => setPhotoMsg(""), 3000);
    } catch { /* keep UI */ }
  };

  const uploadPostImage = async (slug, file) => {
    if (!file) return;
    setPhotoMsg("");
    try {
      const res = await api.adminUploadPostImage(token, slug, file);
      setBlogPosts((prev) => prev.map((p) => (p.slug === slug ? { ...p, image: res.image } : p)));
      setPhotoMsg(`${t.photoUpdated}: ${slug}`);
      setTimeout(() => setPhotoMsg(""), 3000);
    } catch (err) {
      setPhotoMsg(err.response?.data?.detail || t.couponFail);
    }
  };

  const resetPostImage = async (slug) => {
    try {
      const res = await api.adminResetPostImage(token, slug);
      setBlogPosts((prev) => prev.map((p) => (p.slug === slug ? { ...p, image: res.image } : p)));
    } catch { /* keep UI */ }
  };

  const saveWoo = async () => {
    setWooBusy("save");
    setWooMsg("");
    try {
      await api.adminWooSave(token, wooForm);
      setWoo(await api.adminWooGet(token));
      setWooMsg(t.wooSaved);
    } catch (e) {
      setWooMsg(e.response?.data?.detail || t.couponFail);
    } finally {
      setWooBusy("");
    }
  };

  const wooAction = async (kind, fn, okMsg) => {
    setWooBusy(kind);
    setWooMsg("");
    try {
      const r = await fn();
      const detail = Object.entries(r).filter(([k]) => k !== "ok").map(([k, v]) => `${k}: ${v}`).join(" · ");
      setWooMsg(`${okMsg}${detail ? ` — ${detail}` : ""}`);
      setWoo(await api.adminWooGet(token));
    } catch (e) {
      setWooMsg(e.response?.data?.detail || t.couponFail);
    } finally {
      setWooBusy("");
    }
  };

  const importWooOrders = async () => {
    setWooBusy("orders");
    setWooMsg("");
    setWooImp(null);
    try {
      await api.adminWooImportOrders(token);
      const t0 = Date.now();
      const poll = setInterval(async () => {
        try {
          const st = await api.adminWooImportStatus(token);
          setWooImp(st);
          if (!st.running || Date.now() - t0 > 10 * 60 * 1000) {
            clearInterval(poll);
            setWooBusy("");
            setWooMsg(`${t.wooDone} — ${st.imported ?? 0} ${t.importResult}`);
            api.adminWooGet(token).then(setWoo).catch(() => {});
          }
        } catch {
          clearInterval(poll);
          setWooBusy("");
        }
      }, 3000);
    } catch (e) {
      setWooMsg(e.response?.data?.detail || t.couponFail);
      setWooBusy("");
    }
  };

  const toggleVisibility = async (slug, hidden) => {
    try {
      await api.adminSetVisibility(token, slug, hidden);
      setInventory((prev) => prev.map((p) => (p.slug === slug ? { ...p, hidden } : p)));
    } catch { /* keep UI state */ }
  };


  const importCustomersFile = async (file) => {
    if (!file) return;
    setImportMsg("");
    try {
      const r = await api.adminImportCustomers(token, file);
      setImportMsg(`${r.imported} ${t.importResult} · ${r.updated} ${t.importUpdated} · ${r.skipped} ${t.importSkipped}`);
      setCustomers(await api.adminCustomers(token));
    } catch (e) {
      setImportMsg(e.response?.data?.detail || t.importFail);
    }
  };

  const prepareFlashSale = async () => {
    if (!flashForm.region) return;
    setFlashBusy(true);
    setFlashMsg("");
    try {
      const r = await api.adminFlashSale(token, flashForm);
      setCampForm({ subject: r.subject, body: r.body, audience: "customers", region: r.region });
      setFlashMsg(`${r.code} ${t.flashReady}`);
    } catch (e) {
      setFlashMsg(e.response?.data?.detail || t.flashFail);
    } finally {
      setFlashBusy(false);
    }
  };

  const sendCampaign = async () => {
    setCampBusy(true);
    setCampMsg("");
    try {
      const r = await api.adminSendCampaign(token, campForm);
      setCampMsg(`${r.sent} ${t.campSentL} · ${r.failed} ${t.campFailedL}`);
      setCampForm((p) => ({ subject: "", body: "", audience: p.audience, region: p.region }));
      setCampaigns(await api.adminCampaigns(token));
    } catch (e) {
      setCampMsg(e.response?.data?.detail || t.campFail);
    } finally {
      setCampBusy(false);
    }
  };

  const sendReminders = async () => {
    setSendingReminders(true);
    setReminderResult(null);
    try {
      const res = await api.adminTriggerCartRecovery(token);
      setReminderResult({ sent: res.sent, failed: res.failed });
      setSettings(await api.adminSettings(token));
    } catch {
      setReminderResult({ error: true });
    } finally {
      setSendingReminders(false);
    }
  };

  const toggleAutoReminders = async () => {
    const next = !(settings?.cart_recovery_enabled ?? true);
    setSettings(await api.adminUpdateSettings(token, { cart_recovery_enabled: next }));
  };

  const saveThreshold = async () => {
    const value = Number(thresholdInput);
    if (Number.isNaN(value) || value <= 0) {
      setThresholdMsg(t.invalidThreshold);
      setTimeout(() => setThresholdMsg(""), 3000);
      return;
    }
    setSettings(await api.adminUpdateSettings(token, { vip_threshold: value }));
    setCustomers(await api.adminCustomers(token));
    setThresholdMsg(t.saved);
    setTimeout(() => setThresholdMsg(""), 2500);
  };

  const saveBirthday = async (customer) => {
    const value = bdayEdits[customer.email] ?? customer.birthday ?? "";
    await api.adminSetBirthday(token, customer.email, value);
    setCustomers(await api.adminCustomers(token));
    setBdaySavedFor(customer.email);
    setTimeout(() => setBdaySavedFor(""), 2500);
  };

  const saveBirthdayPcts = async () => {
    const vip = Number(bdayPctInputs.vip);
    const reg = Number(bdayPctInputs.reg);
    if ([vip, reg].some((v) => Number.isNaN(v) || v < 1 || v > 95)) {
      setBdayPctMsg(t.invalidPct);
      setTimeout(() => setBdayPctMsg(""), 3000);
      return;
    }
    setSettings(await api.adminUpdateSettings(token, { vip_birthday_percent: vip, birthday_percent: reg }));
    setBdayPctMsg(t.saved);
    setTimeout(() => setBdayPctMsg(""), 2500);
  };

  const saveLaunchDate = async () => {
    try {
      setSettings(await api.adminUpdateSettings(token, { launch_date: launchDateInput }));
      setLaunchMsg(t.saved);
      setTimeout(() => setLaunchMsg(""), 2500);
    } catch {
      setLaunchMsg(t.couponFail);
      setTimeout(() => setLaunchMsg(""), 3000);
    }
  };

  const saveReviewSettings = async () => {
    setRevMsg("");
    try {
      await api.adminUpdateSettings(token, {
        review_requests_enabled: revForm.review_requests_enabled,
        review_request_days: Number(revForm.review_request_days) || 3,
      });
      setRevMsg(t.saved);
    } catch (e) {
      setRevMsg(e.response?.data?.detail || "Error");
    } finally {
      setTimeout(() => setRevMsg(""), 4000);
    }
  };

  const saveChatNumbers = async () => {
    try {
      setSettings(await api.adminUpdateSettings(token, {
        whatsapp_number: chatForm.whatsapp_number,
        viber_number: chatForm.viber_number,
        company_phone: chatForm.company_phone,
        cod_fee: Number(chatForm.cod_fee) || 0,
        order_notify_email: chatForm.order_notify_email,
      }));
      setChatMsg(t.saved);
      setTimeout(() => setChatMsg(""), 2500);
    } catch {
      setChatMsg(t.couponFail);
      setTimeout(() => setChatMsg(""), 3000);
    }
  };

  const langToggle = (dark = false) => (
    <div className={`flex border ${dark ? "border-white/25" : "border-navy/15"}`}>
      {["it", "en"].map((l) => (
        <button
          key={l}
          data-testid={`admin-lang-${l}`}
          onClick={() => switchLang(l)}
          className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] transition-colors ${
            lang === l
              ? dark ? "bg-gold text-navy" : "bg-navy text-white"
              : dark ? "text-white/60 hover:text-gold" : "text-navy/50 hover:text-gold-dark"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-navy noise-bg pt-32">
        <div className="w-full max-w-sm">
          <div className="mb-4 flex justify-end">{langToggle(true)}</div>
          <form onSubmit={login} data-testid="admin-login-form" className="border border-white/10 bg-white/5 p-10 backdrop-blur-sm">
            <Lock size={28} strokeWidth={1.25} className="text-gold" />
            <h1 className="mt-6 font-display text-3xl font-light text-white">Admin</h1>
            <p className="mt-2 text-xs font-light text-white/40">{t.subtitle}</p>
            <input
              type="email" required placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="admin-email-input" className="input-underline mt-8"
            />
            <input
              type="password" required placeholder="Password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="admin-password-input" className="input-underline mt-4"
            />
            {error && <p data-testid="admin-login-error" className="mt-4 text-xs text-red-300">{error}</p>}
            <button type="submit" data-testid="admin-login-button" className="btn-gold-luxe mt-8 w-full">{t.signIn}</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-surface pt-40">
      <div className="mx-auto max-w-7xl px-6 pb-24">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-4xl font-light text-navy">{t.backOffice}</h1>
          <div className="flex items-center gap-4">
            {langToggle()}
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/50 transition-colors hover:text-red-600">
              <LogOut size={14} /> {t.logout}
            </button>
          </div>
        </div>

        {stats && (
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {[
              { icon: Package, label: t.stats.orders, value: stats.orders_count, tid: "stat-orders" },
              { icon: TrendingUp, label: t.stats.revenue, value: eur(stats.revenue), tid: "stat-revenue" },
              { icon: Mail, label: t.stats.newsletter, value: stats.newsletter_count, tid: "stat-newsletter" },
            ].map(({ icon: Icon, label, value, tid }) => (
              <div key={tid} data-testid={tid} className="border border-navy/10 bg-white p-8">
                <Icon size={20} strokeWidth={1.25} className="text-gold-dark" />
                <p className="mt-5 font-display text-3xl font-light text-navy">{value}</p>
                <p className="label-luxe mt-2 text-navy/40">{label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-3">
          {TABS.map((tabKey) => (
            <button
              key={tabKey}
              data-testid={`admin-tab-${tabKey}`}
              onClick={() => setTab(tabKey)}
              className={`border px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors ${
                tab === tabKey ? "border-navy bg-navy text-white" : "border-navy/15 text-navy/60 hover:border-gold"
              }`}
            >
              {tabKey === "arena" ? "ARENA 3+1" : t.tabs[tabKey]}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          <>
            <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
              <MonthPickerField
                value={exportMonth}
                onChange={setExportMonth}
                testid="orders-export-month"
                lang={lang}
              />
              <button
                onClick={async () => download(await api.adminExportOrders(token, exportMonth || undefined), exportMonth ? `orders-${exportMonth}.csv` : "orders.csv")}
                data-testid="orders-export-button"
                className="btn-outline-luxe !px-5 !py-2.5"
              >
                {t.exportCsv}
              </button>
            </div>
            <div data-testid="admin-orders-table" className="mt-4 overflow-x-auto border border-navy/10 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-navy/10">
                    {t.th.map((h) => (
                      <th key={h} className="px-6 py-4 text-start text-[10px] font-semibold uppercase tracking-[0.15em] text-navy/40">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} data-testid={`admin-order-${o.id}`} className="border-b border-navy/5">
                      <td className="px-6 py-4 font-mono text-xs font-semibold text-gold-dark">{o.id}</td>
                      <td className="px-6 py-4 text-xs text-navy/50">{new Date(o.created_at).toLocaleDateString()}</td>
                      <td className="px-6 py-4 text-xs">{o.customer?.name}<br /><span className="text-navy/40">{o.customer?.email}</span></td>
                      <td className="px-6 py-4 text-xs text-navy/60">{o.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}</td>
                      <td className="px-6 py-4 text-xs font-semibold">{eur(o.total)}</td>
                      <td className="px-6 py-4">
                        <span className={`border px-2 py-1 text-[9px] font-bold uppercase tracking-[0.12em] ${
                          o.status === "cancelled"
                            ? "border-red-300 bg-red-50 text-red-600"
                            : "border-sage/50 bg-sage-light/60 text-sage-dark"
                        }`}>{o.status === "cancelled" ? t.fulfillment.cancelled : o.status}</span>
                      </td>
                      <td className="px-6 py-4">
                        {o.status === "cancelled" ? (
                          <span data-testid={`fulfillment-cancelled-${o.id}`} className="text-[10px] font-semibold uppercase tracking-[0.12em] text-red-500/70">
                            {t.fulfillment.cancelled}
                          </span>
                        ) : (
                        <select
                          data-testid={`fulfillment-select-${o.id}`}
                          value={o.fulfillment || "preparing"}
                          onChange={(e) => setFulfillment(o.id, e.target.value)}
                          className="border border-navy/15 bg-white px-2 py-1.5 text-xs text-navy outline-none transition-colors focus:border-gold"
                        >
                          <option value="preparing">{t.fulfillment.preparing}</option>
                          <option value="shipped">{t.fulfillment.shipped}</option>
                          <option value="delivered">{t.fulfillment.delivered}</option>
                        </select>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "customers" && (
          <div data-testid="admin-customers-table" className="mt-6 border border-navy/10 bg-white">
            <div className="flex flex-wrap items-center gap-3 border-b border-navy/10 px-6 py-4">
              <label data-testid="import-customers-btn" className="btn-outline-luxe !px-4 !py-1.5 cursor-pointer">
                {t.importCustomers}
                <input
                  type="file"
                  accept=".csv,.txt,.xlsx"
                  className="hidden"
                  data-testid="import-customers-input"
                  onChange={(e) => { importCustomersFile(e.target.files?.[0]); e.target.value = ""; }}
                />
              </label>
              {importMsg && <span data-testid="import-customers-msg" className="text-xs font-semibold text-gold-dark">{importMsg}</span>}
            </div>
            {vipMsg && <p data-testid="vip-coupon-msg" className="border-b border-gold/30 bg-gold/10 px-6 py-3 text-xs font-semibold text-gold-dark">{vipMsg}</p>}
            {customers.length === 0 ? (
              <p className="p-8 text-sm text-navy/40">{t.noCustomers}</p>
            ) : (
              customers.map((c) => (
                <div key={c.email} className="border-b border-navy/5">
                  <div className="flex w-full items-center justify-between px-6 py-4 transition-colors hover:bg-surface">
                    <button
                      onClick={() => setExpandedCustomer(expandedCustomer === c.email ? null : c.email)}
                      data-testid={`customer-row-${c.email}`}
                      className="flex items-center gap-3 text-start"
                    >
                      <div>
                        <p className="text-sm font-semibold text-navy">{c.name}</p>
                        <p className="text-xs text-navy/45">{c.email} · {c.city}, {c.country}</p>
                      </div>
                      {c.vip && (
                        <span data-testid={`vip-badge-${c.email}`} className="flex items-center gap-1 border border-gold/50 bg-gold/15 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.12em] text-gold-dark">
                          <Crown size={10} /> VIP
                        </span>
                      )}
                    </button>
                    <div className="flex items-center gap-5">
                      {c.vip && (
                        <button
                          onClick={() => createVipCoupon(c)}
                          data-testid={`vip-coupon-btn-${c.email}`}
                          className="cursor-pointer text-[10px] font-semibold uppercase tracking-[0.14em] text-gold-dark transition-colors hover:text-navy"
                        >
                          {t.vipCoupon}
                        </button>
                      )}
                      <button
                        onClick={() => setExpandedCustomer(expandedCustomer === c.email ? null : c.email)}
                        className="text-end"
                        aria-label="toggle orders"
                      >
                        <p className="text-sm font-semibold text-navy">{eur(c.total_spent)}</p>
                        <p className="text-xs text-navy/45">{c.orders.length} {c.orders.length === 1 ? t.order1 : t.ordersN}</p>
                      </button>
                    </div>
                  </div>
                  {expandedCustomer === c.email && (
                    <div data-testid={`customer-orders-${c.email}`} className="border-t border-navy/5 bg-surface px-6 py-4">
                      <div className="mb-3 flex flex-wrap items-center gap-3 border-b border-navy/5 pb-3">
                        <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/50">{t.birthday}</label>
                        <DatePickerField
                          value={bdayEdits[c.email] ?? c.birthday ?? ""}
                          onChange={(v) => setBdayEdits((prev) => ({ ...prev, [c.email]: v }))}
                          testid={`birthday-input-${c.email}`}
                        />
                        <button
                          onClick={() => saveBirthday(c)}
                          data-testid={`birthday-save-${c.email}`}
                          className="btn-outline-luxe !px-4 !py-1.5"
                        >
                          {t.save}
                        </button>
                        {bdaySavedFor === c.email && (
                          <span data-testid={`birthday-saved-${c.email}`} className="text-xs font-semibold text-gold-dark">{t.bdaySaved}</span>
                        )}
                      </div>
                      {c.orders.map((o) => (
                        <div key={o.id} className="flex items-center justify-between border-b border-navy/5 py-2.5 text-xs last:border-0">
                          <span className="font-mono font-semibold text-gold-dark">{o.id}</span>
                          <span className="text-navy/50">{new Date(o.date).toLocaleDateString()}</span>
                          <span className="text-navy/60">{o.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}</span>
                          <span className="font-semibold text-navy">{eur(o.total)}</span>
                          <span className="border border-sage/50 bg-sage-light/60 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-sage-dark">{t.fulfillment[o.fulfillment] || o.fulfillment}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {tab === "newsletter" && (
          <>
            <div className="mt-6 flex justify-end">
              <button onClick={async () => download(await api.adminExportNewsletter(token), "newsletter.csv")} data-testid="admin-export-csv-button" className="btn-outline-luxe !px-5 !py-2.5">
                {t.exportCsv}
              </button>
            </div>
            <div data-testid="admin-newsletter-table" className="mt-4 border border-navy/10 bg-white">
              {subs.length === 0 ? (
                <p className="p-8 text-sm text-navy/40">{t.noSubs}</p>
              ) : (
                subs.map((s) => (
                  <div key={s.email} data-testid={`admin-sub-${s.email}`} className="flex items-center justify-between border-b border-navy/5 px-6 py-4 text-sm">
                    <span className="text-navy">{s.email}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-navy/40">{new Date(s.subscribed_at).toLocaleDateString()}</span>
                      <button
                        type="button"
                        data-testid={`newsletter-delete-${s.email}`}
                        onClick={async () => {
                          await api.adminDeleteSubscriber(token, s.email).catch(() => {});
                          setSubs((prev) => prev.filter((x) => x.email !== s.email));
                        }}
                        className="text-[10px] font-semibold uppercase tracking-[0.12em] text-navy/40 transition-colors hover:text-red-600"
                      >
                        {t.deleteLabel}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {tab === "coupons" && (
          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            <form onSubmit={createCoupon} data-testid="coupon-form" className="border border-navy/10 bg-white p-8">
              <div className="flex items-center gap-2">
                <Percent size={16} className="text-gold-dark" />
                <h2 className="label-luxe text-navy">{t.newCoupon}</h2>
              </div>
              <input
                required placeholder={t.codePh} value={couponForm.code}
                onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                data-testid="coupon-code-input" className="input-luxe mt-6 font-mono uppercase tracking-wider"
              />
              <div className="mt-4 flex gap-3">
                <select
                  value={couponForm.type}
                  onChange={(e) => setCouponForm({ ...couponForm, type: e.target.value })}
                  data-testid="coupon-type-select" className="input-luxe"
                >
                  <option value="percent">% off</option>
                  <option value="amount">€ off</option>
                </select>
                <input
                  required type="number" min="1" step="0.5" placeholder={couponForm.type === "percent" ? "10" : "5.00"}
                  value={couponForm.value}
                  onChange={(e) => setCouponForm({ ...couponForm, value: e.target.value })}
                  data-testid="coupon-value-input" className="input-luxe"
                />
              </div>
              <button type="submit" data-testid="coupon-create-button" className="btn-primary-luxe mt-6 w-full">{t.create}</button>
              {couponMsg && <p data-testid="coupon-message" className="mt-4 text-xs text-navy/60">{couponMsg}</p>}
            </form>
            <div data-testid="coupons-table" className="border border-navy/10 bg-white lg:col-span-2">
              {coupons.map((c) => (
                <div key={c.id} data-testid={`coupon-row-${c.code}`} className="flex items-center justify-between border-b border-navy/5 px-6 py-4 text-sm">
                  <span className="font-mono font-semibold text-navy">{c.code}</span>
                  <span className="text-xs text-navy/50">
                    {c.percent_off ? `${c.percent_off}% off` : c.amount_off ? `€${c.amount_off} off` : "—"}
                  </span>
                  {c.active ? (
                    <button onClick={() => deactivateCoupon(c.id)} data-testid={`coupon-deactivate-${c.code}`} className="text-[10px] font-semibold uppercase tracking-[0.14em] text-red-500 transition-colors hover:text-red-700">
                      {t.deactivate}
                    </button>
                  ) : (
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/30">{t.inactive}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "inventory" && (
          <div data-testid="inventory-table" className="mt-6 overflow-x-auto border border-navy/10 bg-white">
            <div className="border-b border-navy/10 px-6 py-4">
              <div className="flex items-center gap-3">
                <h3 className="label-luxe text-navy">{t.photoSection}</h3>
                {photoMsg && <span data-testid="photo-msg" className="text-xs font-semibold text-gold-dark">{photoMsg}</span>}
              </div>
              <div className="mt-4 flex flex-wrap gap-4">
                {inventory.map((p) => (
                  <div key={p.slug} data-testid={`photo-card-${p.slug}`} className="flex w-44 flex-col items-center gap-2 border border-navy/10 p-3">
                    <img
                      src={productImages[p.slug] || p.image}
                      alt={p.slug}
                      data-testid={`photo-img-${p.slug}`}
                      className="h-16 w-16 object-cover"
                    />
                    <span className="max-w-24 truncate text-[10px] text-navy/60">{p.i18n?.en?.name}</span>
                    <input
                      type="text"
                      defaultValue={(p.certifications || []).join(", ")}
                      onBlur={async (e) => {
                        const certs = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                        if (certs.join(",") !== (p.certifications || []).join(",")) {
                          try { await api.adminSetCerts(token, p.slug, certs); } catch { /* keep */ }
                        }
                      }}
                      placeholder="GMP, EU Registered"
                      title="Certificazioni (separate da virgola)"
                      data-testid={`certs-input-${p.slug}`}
                      className="w-full border border-navy/10 px-1.5 py-1 text-[9px] text-navy/60 outline-none focus:border-gold"
                    />
                    <div className="flex items-center gap-1.5">
                      {p.wc_id ? <span className="border border-navy/15 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.1em] text-navy/50">WC</span> : null}
                      {p.hidden ? (
                        <span data-testid={`hidden-badge-${p.slug}`} className="border border-red-300 bg-red-50 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.1em] text-red-600">{t.wooHiddenBadge}</span>
                      ) : null}
                      <button
                        type="button"
                        data-testid={`visibility-toggle-${p.slug}`}
                        onClick={() => toggleVisibility(p.slug, !p.hidden)}
                        className="text-[9px] font-semibold uppercase tracking-[0.12em] text-gold-dark transition-colors hover:text-navy"
                      >
                        {p.hidden ? t.wooShow : t.wooHide}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <label
                        data-testid={`photo-btn-${p.slug}`}
                        className="cursor-pointer text-[10px] font-semibold uppercase tracking-[0.12em] text-gold-dark transition-colors hover:text-navy"
                      >
                        {t.changePhoto}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="hidden"
                          data-testid={`photo-input-${p.slug}`}
                          onChange={(e) => { uploadPhoto(p.slug, e.target.files?.[0]); e.target.value = ""; }}
                        />
                      </label>
                      {(productImages[p.slug] || "").startsWith("/api/uploads/") && (
                        <button
                          type="button"
                          data-testid={`photo-reset-${p.slug}`}
                          onClick={() => resetPhoto(p.slug)}
                          className="text-[10px] font-semibold uppercase tracking-[0.12em] text-navy/40 transition-colors hover:text-red-600"
                        >
                          {t.resetPhoto}
                        </button>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center justify-center gap-1.5">
                      {(productGalleries[p.slug] || []).map((url, gi) => (
                        <span key={url} className="relative">
                          <img src={url} alt="" data-testid={`gallery-img-${p.slug}-${gi}`} className="h-9 w-9 border border-navy/10 object-cover" />
                          <button
                            type="button"
                            data-testid={`gallery-remove-${p.slug}-${gi}`}
                            onClick={() => removeGalleryImage(p.slug, url)}
                            aria-label="Remove gallery photo"
                            className="absolute -end-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-navy text-[9px] leading-none text-white"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <label
                        data-testid={`gallery-add-${p.slug}`}
                        className="flex h-9 w-9 cursor-pointer items-center justify-center border border-dashed border-navy/25 text-xs text-gold-dark transition-colors hover:border-gold"
                      >
                        +
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="hidden"
                          data-testid={`gallery-input-${p.slug}`}
                          onChange={(e) => { addGalleryImage(p.slug, e.target.files?.[0]); e.target.value = ""; }}
                        />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-navy/10">
                  {t.invTh.map((h) => (
                    <th key={h} className="px-6 py-4 text-start text-[10px] font-semibold uppercase tracking-[0.15em] text-navy/40">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inventory.flatMap((p) =>
                  p.variants.map((v) => {
                    const key = `${p.slug}:${v.caps}`;
                    const low = (v.stock ?? 0) < 10;
                    return (
                      <tr key={key} data-testid={`inventory-${key}`} className="border-b border-navy/5">
                        <td className="px-6 py-4 text-xs font-semibold text-navy">{p.i18n?.en?.name}</td>
                        <td className="px-6 py-4 text-xs text-navy/60">{v.caps} {p.unit || "caps"}</td>
                        <td className="px-6 py-4 text-xs text-navy/60">{eur(v.price)}</td>
                        <td className="px-6 py-4">
                          <input
                            type="number" min="0" defaultValue={v.stock}
                            onChange={(e) => setStockEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                            data-testid={`stock-input-${key}`}
                            className="w-20 border border-navy/15 px-2 py-1.5 text-xs outline-none focus:border-gold"
                          />
                        </td>
                        <td className="px-6 py-4">
                          {low ? (
                            <span data-testid={`low-stock-${key}`} className="border border-red-300 bg-red-50 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.12em] text-red-600">{t.low}</span>
                          ) : (
                            <span className="border border-sage/50 bg-sage-light/60 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.12em] text-sage-dark">{t.okLabel}</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <button onClick={() => saveStock(p.slug, v.caps)} data-testid={`stock-save-${key}`} className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gold-dark transition-colors hover:text-navy">
                            {t.save}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            <div data-testid="site-media-section" className="border-t border-navy/10 px-6 py-6">
              <h3 className="label-luxe text-navy">{t.siteSection}</h3>
              {SITE_MEDIA_GROUPS.map((g) => (
                <div key={g.key} className="mt-5" data-testid={`site-media-group-${g.key}`}>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-navy/40">{t.siteGroups[g.key]}</p>
                  <div className="mt-2 flex flex-wrap gap-4">
                    {g.slots.map((slot) => {
                      const custom = Boolean(siteMediaSlots[slot]);
                      return (
                        <div key={slot} data-testid={`site-media-${slot}`} className="flex w-28 flex-col items-center gap-2 border border-navy/10 p-2.5">
                          <div className="relative">
                            {siteMediaSlots[slot] || SITE_IMAGE_DEFAULTS[slot] ? (
                              <img
                                src={siteMediaSlots[slot] || SITE_IMAGE_DEFAULTS[slot]}
                                alt={slot}
                                data-testid={`site-media-img-${slot}`}
                                className="h-16 w-24 object-cover"
                              />
                            ) : (
                              <span data-testid={`site-media-img-${slot}`} className="flex h-16 w-24 items-center justify-center border border-gold/60 font-display text-lg text-gold-dark">AP</span>
                            )}
                            {custom && (
                              <span data-testid={`site-media-custom-${slot}`} className="absolute start-1 top-1 bg-gold px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.1em] text-navy">{t.customBadge}</span>
                            )}
                          </div>
                          <span className="max-w-24 truncate text-[9px] font-semibold text-navy/60">{t.slotLabels[slot] || slot}</span>
                          <div className="flex items-center gap-2">
                            <label
                              data-testid={`site-media-btn-${slot}`}
                              className="cursor-pointer text-[9px] font-semibold uppercase tracking-[0.12em] text-gold-dark transition-colors hover:text-navy"
                            >
                              {t.changePhoto}
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                className="hidden"
                                data-testid={`site-media-input-${slot}`}
                                onChange={(e) => { uploadSiteMedia(slot, e.target.files?.[0]); e.target.value = ""; }}
                              />
                            </label>
                            {custom && (
                              <button
                                type="button"
                                data-testid={`site-media-reset-${slot}`}
                                onClick={() => resetSiteMedia(slot)}
                                className="text-[9px] font-semibold uppercase tracking-[0.12em] text-navy/40 transition-colors hover:text-red-600"
                              >
                                {t.resetPhoto}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div className="mt-6" data-testid="blog-photo-section">
                <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-navy/40">{t.blogSection}</p>
                <div className="mt-2 flex flex-wrap gap-4">
                  {blogPosts.map((post) => {
                    const custom = (post.image || "").startsWith("/api/uploads/");
                    return (
                      <div key={post.slug} data-testid={`blog-photo-${post.slug}`} className="flex w-44 flex-col items-center gap-2 border border-navy/10 p-2.5">
                        <div className="relative">
                          <img src={post.image} alt={post.slug} data-testid={`blog-photo-img-${post.slug}`} className="h-20 w-40 object-cover" />
                          {custom && (
                            <span data-testid={`blog-photo-custom-${post.slug}`} className="absolute start-1 top-1 bg-gold px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.1em] text-navy">{t.customBadge}</span>
                          )}
                        </div>
                        <span className="w-full truncate text-center text-[9px] font-semibold text-navy/60">{post.i18n?.en?.title || post.slug}</span>
                        <div className="flex items-center gap-2">
                          <label
                            data-testid={`blog-photo-btn-${post.slug}`}
                            className="cursor-pointer text-[9px] font-semibold uppercase tracking-[0.12em] text-gold-dark transition-colors hover:text-navy"
                          >
                            {t.changePhoto}
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              className="hidden"
                              data-testid={`blog-photo-input-${post.slug}`}
                              onChange={(e) => { uploadPostImage(post.slug, e.target.files?.[0]); e.target.value = ""; }}
                            />
                          </label>
                          {custom && (
                            <button
                              type="button"
                              data-testid={`blog-photo-reset-${post.slug}`}
                              onClick={() => resetPostImage(post.slug)}
                              className="text-[9px] font-semibold uppercase tracking-[0.12em] text-navy/40 transition-colors hover:text-red-600"
                            >
                              {t.resetPhoto}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <div data-testid="admin-reviews-list" className="mt-6 border border-navy/10 bg-white">
            {reviews.length === 0 ? (
              <p className="p-8 text-sm text-navy/40">{t.noReviews}</p>
            ) : (
              reviews.map((r, i) => (
                <div key={r.id || i} data-testid={`admin-review-${i}`} className="flex items-start justify-between gap-6 border-b border-navy/5 px-6 py-5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} size={12} className={s <= r.rating ? "fill-gold text-gold" : "text-navy/20"} />
                      ))}
                    </div>
                    <p className="mt-2 font-display text-base font-light italic text-navy/75">“{r.i18n?.en?.text}”</p>
                    <p className="mt-2 text-xs text-navy/45">{r.name} · {r.product_slug} · {r.date}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    {r.approved ? (
                      <span className="border border-sage/50 bg-sage-light/60 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.12em] text-sage-dark">{t.approved}</span>
                    ) : (
                      <>
                        <button
                          onClick={async () => {
                            await api.adminApproveReview(token, r.id);
                            setReviews((prev) => prev.map((x) => (x.id === r.id ? { ...x, approved: true } : x)));
                          }}
                          data-testid={`review-approve-${i}`}
                          className="text-[10px] font-semibold uppercase tracking-[0.14em] text-sage-dark transition-colors hover:text-navy"
                        >
                          {t.approve}
                        </button>
                        <button
                          onClick={async () => {
                            await api.adminDeleteReview(token, r.id);
                            setReviews((prev) => prev.filter((x) => x.id !== r.id));
                          }}
                          data-testid={`review-delete-${i}`}
                          className="text-[10px] font-semibold uppercase tracking-[0.14em] text-red-500 transition-colors hover:text-red-700"
                        >
                          {t.deleteLabel}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "campaigns" && (
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            {regions.length > 0 && (
              <div data-testid="flash-sale-card" className="border border-gold/30 bg-gold/5 p-6 lg:col-span-2">
                <h2 className="label-luxe flex items-center gap-2 text-navy"><Zap size={14} className="text-gold-dark" /> {t.flashTitle}</h2>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <select
                    value={flashForm.region}
                    onChange={(e) => setFlashForm((p) => ({ ...p, region: e.target.value }))}
                    data-testid="flash-region"
                    className="border border-navy/15 bg-white px-3 py-2 text-xs text-navy outline-none focus:border-gold"
                  >
                    <option value="">{t.campAllRegions}</option>
                    {regions.map((r) => (
                      <option key={r.region} value={r.region}>{`${r.region} (${r.count})`}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min={5}
                    max={50}
                    value={flashForm.percent}
                    onChange={(e) => setFlashForm((p) => ({ ...p, percent: Number(e.target.value) }))}
                    data-testid="flash-percent"
                    title={t.flashPercent}
                    className="w-24 border border-navy/15 bg-white px-3 py-2 text-xs text-navy outline-none focus:border-gold"
                  />
                  <button onClick={prepareFlashSale} disabled={flashBusy || !flashForm.region} data-testid="flash-sale-btn" className="btn-gold-luxe !px-5 !py-2 disabled:opacity-50">
                    <span className="inline-flex items-center gap-2"><Zap size={12} /> {flashBusy ? t.sending : t.flashBtn}</span>
                  </button>
                  {flashMsg && <span data-testid="flash-sale-msg" className="text-xs font-semibold text-gold-dark">{flashMsg}</span>}
                </div>
              </div>
            )}
            <div data-testid="campaign-form" className="border border-navy/10 bg-white p-6">
              <h2 className="label-luxe flex items-center gap-2 text-navy"><Mail size={14} className="text-gold-dark" /> {t.campaignSection}</h2>
              <input
                type="text"
                value={campForm.subject}
                onChange={(e) => setCampForm((p) => ({ ...p, subject: e.target.value }))}
                placeholder={t.campSubject}
                data-testid="campaign-subject"
                className="mt-4 w-full border border-navy/15 px-3 py-2 text-sm text-navy outline-none focus:border-gold"
              />
              <textarea
                value={campForm.body}
                onChange={(e) => setCampForm((p) => ({ ...p, body: e.target.value }))}
                placeholder={t.campBody}
                data-testid="campaign-body"
                rows={7}
                className="mt-3 w-full border border-navy/15 px-3 py-2 text-sm text-navy outline-none focus:border-gold"
              />
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <select
                  value={campForm.audience}
                  onChange={(e) => setCampForm((p) => ({ ...p, audience: e.target.value }))}
                  data-testid="campaign-audience"
                  className="border border-navy/15 bg-white px-3 py-2 text-xs text-navy outline-none focus:border-gold"
                >
                  <option value="newsletter">{t.audNews}</option>
                  <option value="customers">{t.audCust}</option>
                  <option value="all">{t.audAll}</option>
                </select>
                {regions.length > 0 && (
                  <select
                    value={campForm.region}
                    onChange={(e) => setCampForm((p) => ({ ...p, region: e.target.value }))}
                    data-testid="campaign-region"
                    title={t.campRegion}
                    className="border border-navy/15 bg-white px-3 py-2 text-xs text-navy outline-none focus:border-gold"
                  >
                    <option value="">{t.campAllRegions}</option>
                    {regions.map((r) => (
                      <option key={r.region} value={r.region}>{`${r.region} (${r.count})`}</option>
                    ))}
                  </select>
                )}
                <button onClick={sendCampaign} disabled={campBusy} data-testid="campaign-send" className="btn-gold-luxe !px-5 !py-2 disabled:opacity-50">
                  <span className="inline-flex items-center gap-2"><Send size={12} /> {campBusy ? t.sending : t.campSend}</span>
                </button>
                {campMsg && <span data-testid="campaign-msg" className="text-xs font-semibold text-gold-dark">{campMsg}</span>}
              </div>
            </div>
            <div data-testid="campaign-history" className="border border-navy/10 bg-white">
              <div className="border-b border-navy/10 px-6 py-4">
                <h2 className="label-luxe text-navy">{t.campHistory}</h2>
              </div>
              {campaigns.length === 0 ? (
                <p data-testid="campaign-empty" className="p-6 text-sm text-navy/40">{t.noCampaigns}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="label-luxe border-b border-navy/10 text-navy/45">
                        {t.campTh.map((h) => (
                          <th key={h} className="px-4 py-3 font-normal">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((c, i) => (
                        <tr key={`${c.sent_at}-${i}`} data-testid={`campaign-row-${i}`} className="border-b border-navy/5 transition-colors hover:bg-surface">
                          <td className="px-4 py-3 text-xs text-navy/50">{new Date(c.sent_at).toLocaleString()}</td>
                          <td className="px-4 py-3 text-xs font-semibold text-navy">{c.subject}</td>
                          <td className="px-4 py-3 text-xs text-navy/60">{t.audLabels[c.audience] || c.audience}</td>
                          <td className="px-4 py-3 text-xs text-navy/60">{c.recipients}</td>
                          <td className="px-4 py-3 text-xs text-sage-dark">{c.sent}</td>
                          <td className="px-4 py-3 text-xs text-red-600/70">{c.failed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "report" && report && (
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div data-testid="report-products" className="border border-navy/10 bg-white p-8">
              <h2 className="label-luxe text-navy">{t.salesByProduct}</h2>
              <div className="mt-6 space-y-5">
                {report.per_product.map((prod) => {
                  const max = Math.max(...report.per_product.map((x) => x.revenue), 1);
                  return (
                    <div key={prod.name}>
                      <div className="flex justify-between text-xs text-navy/70">
                        <span className="font-semibold">{prod.name}</span>
                        <span>{prod.qty} {t.sold} · {eur(prod.revenue)}</span>
                      </div>
                      <div className="mt-1.5 h-1.5 bg-navy/5">
                        <div className="h-1.5 bg-gold transition-all duration-700" style={{ width: `${(prod.revenue / max) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div data-testid="report-months" className="border border-navy/10 bg-white p-8">
              <h2 className="label-luxe text-navy">{t.revenueByMonth}</h2>
              <div className="mt-6 space-y-5">
                {report.per_month.map((m) => {
                  const max = Math.max(...report.per_month.map((x) => x.revenue), 1);
                  return (
                    <div key={m.month}>
                      <div className="flex justify-between text-xs text-navy/70">
                        <span className="font-mono font-semibold">{m.month}</span>
                        <span>{eur(m.revenue)}</span>
                      </div>
                      <div className="mt-1.5 h-1.5 bg-navy/5">
                        <div className="h-1.5 bg-navy transition-all duration-700" style={{ width: `${(m.revenue / max) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {tab === "arena" && <AdminArenaCampaign token={token} />}

        {tab === "email" && (
          <div data-testid="admin-email-tab" className="space-y-8">
            <div className="grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-4">
              {[
                emailCenter?.campaign?.sent ?? "—",
                emailCenter?.campaign?.unique_opens ?? "—",
                emailCenter ? `${emailCenter.campaign.open_rate}%` : "—",
                emailCenter?.campaign?.clicks ?? "—",
              ].map((v, i) => (
                <div key={i} data-testid={`email-kpi-${i}`} className="bg-white p-6">
                  <p className="font-display text-3xl font-light text-navy">{v}</p>
                  <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy/45">{t.emailKpis[i]}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-navy/40">{t.opensNote}</p>

            <div className="border border-navy/10 bg-white">
              <h2 className="label-luxe border-b border-navy/10 px-6 py-4 text-navy">{t.recentOpens}</h2>
              {!emailCenter || emailCenter.recent_opens.length === 0 ? (
                <p className="px-6 py-8 text-xs text-navy/40">{t.noOpens}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <tbody>
                      {emailCenter.recent_opens.map((o, i) => (
                        <tr key={i} data-testid={`email-open-${i}`} className="border-b border-navy/5">
                          <td className="px-6 py-3 text-xs">{o.email}</td>
                          <td className="px-6 py-3 text-xs text-navy/50">{o.at ? new Date(o.at).toLocaleString() : "—"}</td>
                          <td className="px-6 py-3 font-mono text-xs text-gold-dark">×{o.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="border border-navy/10 bg-white">
              <h2 className="label-luxe border-b border-navy/10 px-6 py-4 text-navy">{t.settingsSections.history}</h2>
              {!emailCenter || emailCenter.log.length === 0 ? (
                <p className="px-6 py-8 text-xs text-navy/40">{t.noLog}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-navy/10">
                        {t.historyTh.slice(0, 4).map((h) => (
                          <th key={h} className="px-6 py-3 text-[9px] font-bold uppercase tracking-[0.16em] text-navy/40">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {emailCenter.log.map((l) => (
                        <tr key={l.id} data-testid={`email-center-${l.id}`} className="border-b border-navy/5">
                          <td className="px-6 py-3 text-xs text-navy/50">{new Date(l.created_at).toLocaleString()}</td>
                          <td className="px-6 py-3 text-xs">{l.email}</td>
                          <td className="px-6 py-3 text-xs text-navy/60">{t.typeLabels[l.type] || l.type}</td>
                          <td className="px-6 py-3">
                            <span className={`border px-2 py-1 text-[9px] font-bold uppercase tracking-[0.12em] ${
                              l.status === "sent"
                                ? "border-sage/50 bg-sage-light/60 text-sage-dark"
                                : "border-red-300 bg-red-50 text-red-600"
                            }`}>
                              {t.statusLabels[l.status] || l.status}
                            </span>
                            {l.opened && (
                              <span className="ml-2 border border-gold/60 bg-gold/15 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.12em] text-gold-dark">
                                {t.openedBadge}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "settings" && (
          <div data-testid="admin-settings-tab" className="mt-6 grid gap-6 lg:grid-cols-2">
            <div data-testid="settings-loyalty" className="border border-navy/10 bg-white p-6">
              <h2 className="label-luxe text-navy">{t.settingsSections.loyalty}</h2>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/50">{t.vipThreshold} (€)</label>
                <input
                  type="number" min="1" value={thresholdInput}
                  onChange={(e) => setThresholdInput(e.target.value)}
                  data-testid="vip-threshold-input"
                  className="w-24 border border-navy/15 bg-white px-3 py-1.5 text-xs text-navy outline-none focus:border-gold"
                />
                <button onClick={saveThreshold} data-testid="vip-threshold-save" className="btn-outline-luxe !px-4 !py-1.5">
                  {t.save}
                </button>
                {thresholdMsg && <span data-testid="vip-threshold-msg" className="text-xs font-semibold text-gold-dark">{thresholdMsg}</span>}
              </div>
            </div>

            <div data-testid="settings-reminders" className="border border-navy/10 bg-white p-6">
              <h2 className="label-luxe text-navy">{t.settingsSections.reminders}</h2>
              {settings === null ? (
                <span className="mt-4 inline-block px-3 py-2 text-[10px] text-navy/30">—</span>
              ) : (
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    onClick={toggleAutoReminders}
                    data-testid="auto-reminders-toggle"
                    className={`flex items-center gap-2 border px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em] transition-colors ${
                      settings.cart_recovery_enabled ?? true
                        ? "border-sage/60 bg-sage-light/60 text-sage-dark"
                        : "border-navy/15 text-navy/40"
                    }`}
                  >
                    <span className={`inline-block h-2 w-2 rounded-full ${settings.cart_recovery_enabled ?? true ? "bg-sage-dark" : "bg-navy/30"}`} />
                    {t.autoReminders}: {(settings.cart_recovery_enabled ?? true) ? "ON" : "OFF"}
                  </button>
                  <span data-testid="auto-reminders-last-run" className="text-[10px] text-navy/40">
                    {t.lastRun}: {settings.cart_recovery_last_run ? new Date(settings.cart_recovery_last_run).toLocaleString() : t.never}
                  </span>
                  <button onClick={sendReminders} disabled={sendingReminders} data-testid="cart-reminders-button" className="btn-outline-luxe !px-5 !py-2.5 disabled:opacity-50">
                    {sendingReminders ? t.sending : t.cartReminders}
                  </button>
                  {reminderResult && (
                    <span data-testid="reminders-result" className="text-xs font-semibold text-gold-dark">
                      {reminderResult.error
                        ? t.remindersFail
                        : `${t.remindersSent}: ${reminderResult.sent}${reminderResult.failed ? ` · ${t.remindersFailed}: ${reminderResult.failed}` : ""}`}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div data-testid="settings-birthdays" className="border border-navy/10 bg-white p-6">
              <h2 className="label-luxe text-navy">{t.settingsSections.birthdays}</h2>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/50">{t.vipBdayPct}</label>
                <input
                  type="number" min="1" max="95" value={bdayPctInputs.vip}
                  onChange={(e) => setBdayPctInputs((p) => ({ ...p, vip: e.target.value }))}
                  data-testid="vip-bday-pct-input"
                  className="w-20 border border-navy/15 bg-white px-3 py-1.5 text-xs text-navy outline-none focus:border-gold"
                />
                <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/50">{t.bdayPct}</label>
                <input
                  type="number" min="1" max="95" value={bdayPctInputs.reg}
                  onChange={(e) => setBdayPctInputs((p) => ({ ...p, reg: e.target.value }))}
                  data-testid="bday-pct-input"
                  className="w-20 border border-navy/15 bg-white px-3 py-1.5 text-xs text-navy outline-none focus:border-gold"
                />
                <button onClick={saveBirthdayPcts} data-testid="bday-pct-save" className="btn-outline-luxe !px-4 !py-1.5">
                  {t.save}
                </button>
                {bdayPctMsg && <span data-testid="bday-pct-msg" className="text-xs font-semibold text-gold-dark">{bdayPctMsg}</span>}
              </div>
              <p className="mt-3 text-xs text-navy/40">{t.birthdayNote}</p>
            </div>

            <div data-testid="settings-launch" className="border border-navy/10 bg-white p-6">
              <h2 className="label-luxe text-navy">{t.settingsSections.launch}</h2>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/50">{t.launchDate}</label>
                <DatePickerField value={launchDateInput} onChange={setLaunchDateInput} testid="launch-date" fromYear={2026} toYear={2030} />
                <button onClick={saveLaunchDate} data-testid="launch-date-save" className="btn-outline-luxe !px-4 !py-1.5">
                  {t.save}
                </button>
                {launchMsg && <span data-testid="launch-date-msg" className="text-xs font-semibold text-gold-dark">{launchMsg}</span>}
              </div>
              <p className="mt-3 text-xs text-navy/40">{t.launchNote}</p>
            </div>

            <div data-testid="settings-chat" className="border border-navy/10 bg-white p-6">
              <h2 className="label-luxe text-navy">{t.settingsSections.chat}</h2>
              <div className="mt-4 space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="w-36 text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/50">{t.chatWa}</label>
                  <input
                    type="tel"
                    value={chatForm.whatsapp_number}
                    onChange={(e) => setChatForm((p) => ({ ...p, whatsapp_number: e.target.value }))}
                    placeholder="+30…"
                    data-testid="chat-whatsapp-input"
                    className="w-52 border border-navy/15 px-3 py-1.5 text-xs text-navy outline-none focus:border-gold"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="w-36 text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/50">{t.chatViber}</label>
                  <input
                    type="tel"
                    value={chatForm.viber_number}
                    onChange={(e) => setChatForm((p) => ({ ...p, viber_number: e.target.value }))}
                    placeholder="+30…"
                    data-testid="chat-viber-input"
                    className="w-52 border border-navy/15 px-3 py-1.5 text-xs text-navy outline-none focus:border-gold"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="w-36 text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/50">{t.chatPhone}</label>
                  <input
                    type="tel"
                    value={chatForm.company_phone}
                    onChange={(e) => setChatForm((p) => ({ ...p, company_phone: e.target.value }))}
                    placeholder="+302108100000"
                    data-testid="chat-phone-input"
                    className="w-52 border border-navy/15 px-3 py-1.5 text-xs text-navy outline-none focus:border-gold"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="w-36 text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/50">{t.chatCodFee}</label>
                  <input
                    type="number"
                    min={0}
                    max={50}
                    step={0.1}
                    value={chatForm.cod_fee}
                    onChange={(e) => setChatForm((p) => ({ ...p, cod_fee: e.target.value }))}
                    placeholder="0.00"
                    data-testid="chat-codfee-input"
                    className="w-32 border border-navy/15 px-3 py-1.5 text-xs text-navy outline-none focus:border-gold"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="w-36 text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/50">{t.chatNotify}</label>
                  <input
                    type="email"
                    value={chatForm.order_notify_email}
                    onChange={(e) => setChatForm((p) => ({ ...p, order_notify_email: e.target.value }))}
                    placeholder="ordini@tuodominio.com"
                    data-testid="chat-notify-input"
                    className="w-52 border border-navy/15 px-3 py-1.5 text-xs text-navy outline-none focus:border-gold"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={saveChatNumbers} data-testid="chat-numbers-save" className="btn-outline-luxe !px-4 !py-1.5">
                    {t.save}
                  </button>
                  {chatMsg && <span data-testid="chat-numbers-msg" className="text-xs font-semibold text-gold-dark">{chatMsg}</span>}
                </div>
              </div>
              <p className="mt-3 text-xs text-navy/40">{t.chatNote}</p>
            </div>

            <div data-testid="settings-stripe" className="border border-navy/10 bg-white p-6">
              <h2 className="label-luxe text-navy">{t.stripeSection}</h2>
              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={async () => {
                    setStripeMsg("");
                    setStripeBusy(true);
                    try {
                      const r = await api.adminStripeSync(token);
                      setStripeMsg(`${t.saved} (${r.mode})`);
                    } catch (e) {
                      setStripeMsg(e.response?.data?.detail || t.couponFail);
                    } finally {
                      setStripeBusy(false);
                      setTimeout(() => setStripeMsg(""), 4000);
                    }
                  }}
                  disabled={stripeBusy}
                  data-testid="stripe-sync-btn"
                  className="btn-outline-luxe !px-4 !py-1.5 disabled:opacity-50"
                >
                  {stripeBusy ? t.sending : t.stripeSync}
                </button>
                {stripeMsg && <span data-testid="stripe-sync-msg" className="text-xs font-semibold text-gold-dark">{stripeMsg}</span>}
              </div>
              <p className="mt-3 text-xs text-navy/40">{t.stripeNote}</p>
            </div>

            <div data-testid="settings-reviews" className="border border-navy/10 bg-white p-6">
              <h2 className="label-luxe text-navy">{t.settingsSections.review}</h2>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-navy">
                  <input
                    type="checkbox"
                    checked={revForm.review_requests_enabled}
                    onChange={(e) => setRevForm((p) => ({ ...p, review_requests_enabled: e.target.checked }))}
                    data-testid="review-enabled-toggle"
                    className="accent-gold-dark"
                  />
                  {t.revEnabled}
                </label>
                <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/50">{t.revDays}</label>
                <input
                  type="number" min="1" max="30"
                  value={revForm.review_request_days}
                  onChange={(e) => setRevForm((p) => ({ ...p, review_request_days: e.target.value }))}
                  data-testid="review-days-input"
                  className="w-20 border border-navy/15 bg-white px-3 py-1.5 text-xs text-navy outline-none focus:border-gold"
                />
                <button onClick={saveReviewSettings} data-testid="review-settings-save" className="btn-outline-luxe !px-4 !py-1.5">
                  {t.save}
                </button>
                {revMsg && <span data-testid="review-settings-msg" className="text-xs font-semibold text-gold-dark">{revMsg}</span>}
              </div>
              <p className="mt-3 text-xs text-navy/40">{t.revNote}</p>
            </div>

            <div data-testid="settings-woocommerce" className="border border-navy/10 bg-white p-6 lg:col-span-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="label-luxe text-navy">{t.wooSection}</h2>
                {woo?.configured && (
                  <span data-testid="woo-status" className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-sage-dark">
                    <Check size={12} /> {t.wooSaved}: {woo.store_url}
                  </span>
                )}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <input
                  type="url"
                  value={wooForm.store_url}
                  onChange={(e) => setWooForm((p) => ({ ...p, store_url: e.target.value }))}
                  placeholder={t.wooUrl}
                  data-testid="woo-url"
                  className="w-64 border border-navy/15 px-3 py-1.5 text-xs text-navy outline-none focus:border-gold"
                />
                <input
                  type="text"
                  value={wooForm.consumer_key}
                  onChange={(e) => setWooForm((p) => ({ ...p, consumer_key: e.target.value }))}
                  placeholder={t.wooKey}
                  data-testid="woo-key"
                  className="w-56 border border-navy/15 px-3 py-1.5 text-xs text-navy outline-none focus:border-gold"
                />
                <input
                  type="password"
                  value={wooForm.consumer_secret}
                  onChange={(e) => setWooForm((p) => ({ ...p, consumer_secret: e.target.value }))}
                  placeholder={woo?.consumer_secret || t.wooSecret}
                  data-testid="woo-secret"
                  className="w-56 border border-navy/15 px-3 py-1.5 text-xs text-navy outline-none focus:border-gold"
                />
                <button onClick={saveWoo} disabled={wooBusy === "save"} data-testid="woo-save" className="btn-gold-luxe !px-4 !py-1.5 disabled:opacity-50">
                  {t.wooSave}
                </button>
              </div>
              {woo?.configured && (
                <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-navy/5 pt-4">
                  <button onClick={() => wooAction("test", () => api.adminWooTest(token), t.wooOk)} disabled={!!wooBusy} data-testid="woo-test" className="btn-outline-luxe !px-4 !py-1.5 disabled:opacity-50">
                    {t.wooTest}
                  </button>
                  <button onClick={() => wooAction("products", () => api.adminWooImport(token, "products"), t.wooDone)} disabled={!!wooBusy} data-testid="woo-import-products" className="btn-outline-luxe !px-4 !py-1.5 disabled:opacity-50">
                    {t.wooImportProducts}
                  </button>
                  <button onClick={() => wooAction("customers", () => api.adminWooImport(token, "customers"), t.wooDone)} disabled={!!wooBusy} data-testid="woo-import-customers" className="btn-outline-luxe !px-4 !py-1.5 disabled:opacity-50">
                    {t.wooImportCustomers}
                  </button>
                  <button onClick={() => wooAction("sync", () => api.adminWooSync(token), t.wooDone)} disabled={!!wooBusy} data-testid="woo-sync" className="btn-outline-luxe !px-4 !py-1.5 disabled:opacity-50">
                    {t.wooSync}
                  </button>
                  <button onClick={importWooOrders} disabled={!!wooBusy} data-testid="woo-import-orders" className="btn-outline-luxe !px-4 !py-1.5 disabled:opacity-50">
                    {t.wooImportOrders}
                  </button>
                  <button onClick={() => wooAction("webhooks", () => api.adminWooWebhooks(token), t.wooDone)} disabled={!!wooBusy} data-testid="woo-webhooks" className="btn-outline-luxe !px-4 !py-1.5 disabled:opacity-50">
                    {t.wooWebhooks}
                  </button>
                  {wooBusy && <span className="text-xs text-navy/40">{t.sending}</span>}
                </div>
              )}
              {wooImp?.running && (
                <p data-testid="woo-import-progress" className="mt-3 text-xs font-semibold text-navy/60">
                  {wooImp.imported} {t.importResult}…
                </p>
              )}
              {wooMsg && <p data-testid="woo-msg" className="mt-3 text-xs font-semibold text-gold-dark">{wooMsg}</p>}
              <p className="mt-3 text-xs text-navy/40">{t.wooNote}</p>
            </div>

            <div data-testid="settings-history" className="border border-navy/10 bg-white lg:col-span-2">
              <h2 className="label-luxe border-b border-navy/10 px-6 py-4 text-navy">{t.settingsSections.history}</h2>
              {emailLog.length === 0 ? (
                <p className="p-8 text-sm text-navy/40">{t.noLog}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-navy/10">
                        {t.historyTh.map((h) => (
                          <th key={h} className="px-6 py-3 text-start text-[10px] font-semibold uppercase tracking-[0.15em] text-navy/40">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {emailLog.map((l) => (
                        <tr key={l.id} data-testid={`email-log-${l.id}`} className="border-b border-navy/5">
                          <td className="px-6 py-3 text-xs text-navy/50">{new Date(l.created_at).toLocaleString()}</td>
                          <td className="px-6 py-3 text-xs">{l.email}</td>
                          <td className="px-6 py-3 text-xs text-navy/60">{t.typeLabels[l.type] || l.type}</td>
                          <td className="px-6 py-3">
                            <span className={`border px-2 py-1 text-[9px] font-bold uppercase tracking-[0.12em] ${
                              l.status === "sent"
                                ? "border-sage/50 bg-sage-light/60 text-sage-dark"
                                : "border-red-300 bg-red-50 text-red-600"
                            }`}>
                              {t.statusLabels[l.status] || l.status}
                            </span>
                          </td>
                          <td className="px-6 py-3 font-mono text-xs text-gold-dark">{l.detail || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
